import { Alert } from "@mui/material";
import { useVirtualizer } from "@tanstack/react-virtual";
import * as React from "react";

import { isAbortError } from "@/shared/errors";
import {
  useMeasure,
  type OnDimensionsChange,
  useDebouncedCallback,
} from "@/shared/hooks";
import { ErrorMonitoringService } from "@/shared/services";
import {
  LayoutItem,
  LogPanelState,
  useVizDispatch,
  actions,
} from "@/shared/state/visualization";

import { type LogPanelSettingsSidebarProps } from "../../PanelSettings/LogPanelSettings";
import { SidebarViewType, useWorkspaceSidebar } from "../../sidebarContext";
import { useWorkspaceTimer } from "../../WorkspaceCtx";
import { useSelectPanel } from "../../WorkspaceCtx/hooks";
import { DataNotFoundMessage } from "../DataNotFoundMessage";
import { NoDataMessage } from "../NoDataMessage";
import { PanelHeader } from "../PanelHeader";
import { PanelLayout } from "../PanelLayout";
import { RenderingError } from "../RenderingError";

import type { ColumnFiltersAction } from "./columnFilters/actions";
import { commitStep } from "./columnFilters/commit";
import { isValuelessCondition } from "./columnFilters/conditionMeta";
import { deriveLiveColumnFilters } from "./columnFilters/derive";
import { generateFilterStageColors } from "./columnFilters/filterStageColors";
import { type ColumnFilter } from "./columnFilters/state";
import { createFilterApplyTracker } from "./filterApplyTracker";
import { getDynamicColumns } from "./getDynamicColumns";
import { IndexState } from "./IndexState";
import type { FilterDisplayMode } from "./LogFilter";
import { LogManager } from "./LogManager";
import styles from "./LogPanel.module.css";
import { estimateTopicMessageCount, skeletonRowCount } from "./logRowCounts";
import { SearchTool } from "./logTools/SearchTool";
import { LogEvent } from "./messaging";
import { resolveSearchVisible } from "./resolveSearchVisible";
import { isRowSelected, type LogSelection } from "./rowSelection";
import { FilterBar } from "./search/FilterBar";
import {
  buildColorMap,
  prepareFilters,
  type PreparedFilters,
} from "./search/highlightUtils";
import { useSearchIndexManager } from "./search/useSearchIndexManager";
import { DEFAULT_LOG_LINE_HEIGHT, Pagination, Row, TableHeader } from "./table";
import {
  buildGridTemplate,
  COLUMN_GAP_PX,
  computeMinGridWidth,
  measureFromHeader,
} from "./table/columnLayout";
import {
  columnResizeReducer,
  createColumnResizeState,
} from "./table/columnResize";
import { useLogs } from "./useLogs";

interface LogPanelProps {
  layout: LayoutItem;
  state: LogPanelState;
}

/**
 * Chosen such that MAX_LOGS_PER_PAGE * DEFAULT_LOG_LINE_HEIGHT is less than
 * the maximum height of a scroll element (which varies browser by browser, but tops out aroun 17M pixels tall).
 */
const MAX_LOGS_PER_PAGE = 250_000;

/**
 * Project virtualized index in pagination-space to actual index in data-space.
 */
function virtualToDataIndex(virtualIndex: number, currentPage: number) {
  return currentPage * MAX_LOGS_PER_PAGE + virtualIndex;
}

const VIRTUAL_GRID_INLINE_STYLES = Object.freeze({
  "--row-height": `${DEFAULT_LOG_LINE_HEIGHT}px`,
}) as React.CSSProperties;

// Stable empty reference so memoized rows bail out when search is hidden
// and filter state hasn't changed.
const EMPTY_PREPARED_FILTERS: PreparedFilters = new Map();

export function LogPanel({ layout, state }: LogPanelProps) {
  const timer = useWorkspaceTimer();
  const sidebar = useWorkspaceSidebar();
  const selectPanel = useSelectPanel();
  const dispatch = useVizDispatch();
  const [logManager, setLogManager] = React.useState<LogManager | null>(null);
  const [loadingState, setLoadingState] =
    React.useState<ReturnType<LogManager["downloadState"]>>("foreground");

  const [error, setError] = React.useState<Error | null>(null);

  // Message::sequence is used as the unique identifier for tracking expanded state
  const [expanded, setExpanded] = React.useState<Set<number>>(new Set());

  // A click selects one row by sequence; playback selects a timestamp, so every
  // row sharing it highlights together (one timestamp can hold many rows).
  const [selected, setSelected] = React.useState<LogSelection | undefined>();

  // Estimated total row count from the topics' message counts. Feeds the
  // large-dataset gate, and (capped) the loading skeleton count below.
  const topicMessageCount = React.useMemo(
    () => estimateTopicMessageCount(state.data),
    [state.data],
  );

  // The number of logs indexed by timestamp only in the log search engine.
  const [indexedLogCount, setIndexedLogCount] = React.useState(0);

  // Incremented whenever matchStates changes in the cache, forcing a re-render
  // even when the index length is unchanged (e.g. filter changes in dim mode).
  const [, setMatchStatesVersion] = React.useState(0);

  // Columns are a pure derivation of the panel's data — the worker computes
  // the same list from the same array, so no async round-trip is involved and
  // saved filters never race an empty column set.
  const dynamicColumns = React.useMemo(
    () => getDynamicColumns(state.data),
    [state.data],
  );

  // The number of logs that the panel should display.
  //
  // If a non-zero number of logs have been loaded, the indexed log count is the
  // source of truth always. The log search engine can apply filters to the log index,
  // which changes the total number of logs that will be shown (even 0).
  //
  // However, during the initial load of the log time index, the indexed
  // log count will be zero. Respecting the indexed log count in this situation means
  // that no logs or loading state will be visible in the panel.
  //
  // As a fallback, show a viewport-filling number of skeleton rows, capped so a
  // heavy topic doesn't size the panel to millions.
  const panelLogCount = React.useMemo(() => {
    if (indexedLogCount === 0 && loadingState) {
      return skeletonRowCount(topicMessageCount);
    }
    return indexedLogCount;
  }, [indexedLogCount, topicMessageCount, loadingState]);

  const [currentPage, setCurrentPage] = React.useState(0); // 0-indexed

  const [searchIndexState, setSearchIndexState] = React.useState(
    IndexState.Empty,
  );

  // Filter-bar visibility is read from panel config on every render rather than
  // latched at mount, so a config write from outside the panel (an AI tool,
  // layout hydration) can open or close the bar on a mounted panel. See
  // resolveSearchVisible for configs saved without the field.
  //
  // Visibility doubles as the filtering switch: while the bar is closed, column
  // headers offer no filter, rows render without highlights, and
  // endFilteringWhenBarClosesOrPanelEmpties disposes the search index so rows
  // come back unfiltered. The filters stay in columnFilters and re-apply once
  // the index rebuilds on reopen.
  const isSearchVisible = resolveSearchVisible(state.config);
  const toggleSearchVisibility = React.useCallback(() => {
    dispatch(
      actions.setLogPanelConfig(state.id, { searchVisible: !isSearchVisible }),
    );
  }, [dispatch, state.id, isSearchVisible]);

  React.useEffect(
    function init() {
      if (error !== null) {
        return;
      }

      const abortController = new AbortController();
      const manager = new LogManager();

      manager.setEventListener(LogEvent.Initialized, () => {
        if (abortController.signal.aborted) {
          return;
        }

        setLogManager(manager);
      });

      manager.setEventListener(LogEvent.Error, (err) => {
        if (abortController.signal.aborted || isAbortError(err)) {
          return;
        }

        ErrorMonitoringService.captureError(err);
        setError(err);
        setLogManager(null);
      });

      manager.setEventListener(LogEvent.NumLogsChanged, ({ numLogs }) => {
        if (abortController.signal.aborted) {
          return;
        }

        setIndexedLogCount(numLogs);
      });

      manager.setOnMatchStatesChanged(() => {
        if (abortController.signal.aborted) {
          return;
        }
        setMatchStatesVersion((v) => v + 1);
      });

      return function cleanUp() {
        abortController.abort();
        manager.dispose();
      };
    },
    [error],
  );

  const dataLoadingTimerHoldRef = React.useRef<symbol | null>(null);
  React.useEffect(
    function holdTimerWhileDataLoading() {
      if (logManager === null) {
        return;
      }

      const abortController = new AbortController();

      logManager.setEventListener(LogEvent.LoadingStateChange, ({ state }) => {
        if (abortController.signal.aborted) {
          return;
        }

        setLoadingState(state);

        const isLoading = state !== false;
        if (isLoading && dataLoadingTimerHoldRef.current === null) {
          dataLoadingTimerHoldRef.current = timer.hold();
        }
        if (!isLoading && dataLoadingTimerHoldRef.current !== null) {
          timer.holdRelease(dataLoadingTimerHoldRef.current);
          dataLoadingTimerHoldRef.current = null;
        }
      });

      return function cleanUp() {
        abortController.abort();
        if (dataLoadingTimerHoldRef.current !== null) {
          timer.holdRelease(dataLoadingTimerHoldRef.current);
          dataLoadingTimerHoldRef.current = null;
        }
      };
    },
    [logManager, timer],
  );

  /**
   * Hold the timer while the search index is building.
   * Building the search index is resource intensive, we don't want playback held up by it.
   */
  const searchIndexBuildingTimerHoldRef = React.useRef<symbol | null>(null);
  React.useEffect(
    function holdTimerWhileSearchIndexBuilding() {
      if (logManager === null) {
        return;
      }

      const abortController = new AbortController();

      logManager.setEventListener(
        LogEvent.SearchIndexStateChange,
        ({ state }) => {
          if (abortController.signal.aborted) {
            return;
          }

          switch (state) {
            case IndexState.Building: {
              if (searchIndexBuildingTimerHoldRef.current === null) {
                searchIndexBuildingTimerHoldRef.current = timer.hold();
              }
              break;
            }
            case IndexState.Built: // FALL THROUGH
            case IndexState.Empty: {
              if (searchIndexBuildingTimerHoldRef.current !== null) {
                timer.holdRelease(searchIndexBuildingTimerHoldRef.current);
                searchIndexBuildingTimerHoldRef.current = null;
              }
              break;
            }
          }

          setSearchIndexState(state);
        },
      );

      return function cleanUp() {
        abortController.abort();
        if (searchIndexBuildingTimerHoldRef.current !== null) {
          timer.holdRelease(searchIndexBuildingTimerHoldRef.current);
          searchIndexBuildingTimerHoldRef.current = null;
        }
      };
    },
    [logManager, timer],
  );

  React.useEffect(
    function setState() {
      if (logManager === null) {
        return;
      }

      const abortController = new AbortController();

      logManager.setState(state.data, abortController.signal);

      return function cleanUp() {
        abortController.abort();
      };
    },
    [state.data, logManager],
  );

  const { buildSearchIndex, disposeSearchIndex, cancelBuild } =
    useSearchIndexManager(logManager);

  const A_LOT_OF_LOGS = 100_000; // 2025-03-17(GM): result of convo w/Benji

  /**
   * Whether we need confirmation before building the search index.
   * FilterBar uses this to decide between auto-showing a progress indicator
   * (small counts) vs. prompting the user with a Build/Dismiss alert (large
   * counts, where building is expensive and may cause memory issues).
   */
  const needsConfirmation =
    logManager !== null &&
    logManager.isSearchIndexEmpty &&
    topicMessageCount > A_LOT_OF_LOGS;

  // A panel with no data has nothing to index, and the worker's search-index
  // build waits on a log-time index it never builds for an empty panel. A build
  // requested without data would sit waiting, then run unprompted over whatever
  // data is added next, even data large enough to need confirmation.
  const hasData = state.data.length > 0;

  // Build the search index automatically when search is visible, the panel has
  // data, and the index is empty (only for small log counts; large log counts
  // are gated on an inline confirmation alert rendered by FilterBar).
  React.useEffect(
    function autoBuildSearchIndexForSmallPanels() {
      if (logManager === null || !isSearchVisible || !hasData) {
        return;
      }

      if (!logManager.isSearchIndexEmpty) {
        return;
      }

      if (!needsConfirmation) {
        buildSearchIndex();
      }
    },
    [logManager, isSearchVisible, hasData, needsConfirmation, buildSearchIndex],
  );

  // Closing the filter bar ends filtering, however it was closed: the toolbar
  // button, a dismissed build prompt, or a config write from outside the panel.
  // Binding this to the value rather than to one handler is what makes hidden
  // mean un-applied — disposeSearchIndex awaits clearFilter, so the rows come
  // back unfiltered in the same step the index is freed. It also frees the
  // memory the index holds.
  //
  // An index the worker still reports as empty has nothing to clear, but a
  // build may be queued behind the log-time load, which the worker does not
  // report until it starts. Cancelling covers that build; it is a no-op for a
  // freshly mounted panel, which starts closed and never requested one.
  //
  // Emptying the panel ends filtering the same way, so a build queued before
  // the data went away cannot fire on the next data added (see hasData).
  React.useEffect(
    function endFilteringWhenBarClosesOrPanelEmpties() {
      if (logManager === null || (isSearchVisible && hasData)) {
        return;
      }
      if (logManager.isSearchIndexEmpty) {
        cancelBuild();
        return;
      }
      void disposeSearchIndex();
    },
    [logManager, isSearchVisible, hasData, disposeSearchIndex, cancelBuild],
  );

  const tableRef = React.useRef<HTMLDivElement>(null);
  const hasInitiatedTimerUpdateRef = React.useRef<boolean>(false);

  const headerRowRef = React.useRef<HTMLDivElement>(null);

  // Column widths live entirely in this reducer — including the rollback for an
  // in-flight drag and the decision of what to persist. See columnResize.ts for
  // why they are one value rather than several kept in step by hand.
  const [resize, resizeDispatch] = React.useReducer(
    columnResizeReducer,
    state.config?.columnWidths ?? {},
    createColumnResizeState,
  );
  const columnWidths = resize.widths;

  // Measuring the header is the reducer's one dependency on the DOM, so the
  // reader is built here and handed in rather than the reducer reaching out.
  const handleResizeStart = React.useCallback(
    (columnId: string, clientX: number) => {
      const headerEl = headerRowRef.current;
      resizeDispatch({
        type: "pointerDown",
        columnId,
        clientX,
        measure:
          headerEl === null ? () => undefined : measureFromHeader(headerEl),
      });
    },
    [],
  );

  const handleResizeMove = React.useCallback(
    (columnId: string, clientX: number) => {
      resizeDispatch({ type: "pointerMove", columnId, clientX });
    },
    [],
  );

  const handleResizeEnd = React.useCallback((columnId: string) => {
    resizeDispatch({ type: "pointerUp", columnId });
  }, []);

  const handleResizeAbort = React.useCallback((columnId: string) => {
    resizeDispatch({ type: "pointerAbort", columnId });
  }, []);

  // The reducer names the widths a transition owes to the saved layout; this is
  // the only place that writes them, so "shown" and "saved" cannot drift apart
  // through some path that forgot to dispatch.
  React.useEffect(
    function persistColumnWidths() {
      if (resize.persist === null) {
        return;
      }
      dispatch(
        actions.setLogPanelConfig(state.id, { columnWidths: resize.persist }),
      );
    },
    [resize.persist, dispatch, state.id],
  );

  // panel.config.columnFilters is the single source of truth for filters.
  // The live state is derived on every config or data change, so external
  // config writes (AI tools, layout hydration) are reflected without a
  // panel-local copy to reconcile. Deriving never writes back — loading a
  // legacy-format config cannot dirty the workspace on its own.
  const liveColumnFilters = React.useMemo(
    () =>
      deriveLiveColumnFilters(
        state.config?.columnFilters,
        state.data,
        dynamicColumns,
      ),
    [state.config?.columnFilters, state.data, dynamicColumns],
  );

  // Commit adapter: applies a filter action to the filters derived above and,
  // when the result genuinely differs, writes it to panel config. Nothing is
  // kept here — the next render re-derives from the config just written.
  // Every action carries a whole edit, so one dispatch is one commit.
  const columnFiltersDispatch = React.useCallback(
    (action: ColumnFiltersAction) => {
      const { filters, changed } = commitStep(liveColumnFilters, action);
      if (changed) {
        // Explicitly clear the legacy searchFilter key so it doesn't persist
        // as stale zombie state in saved configs.
        //
        // A config saved before searchVisible existed gets the field backfilled
        // here, on its first edit. Without it, clearing the last filter empties
        // columnFilters, the absent-field fallback re-evaluates to false, and
        // the bar the user is working in closes itself. Always true: filters
        // are only editable while the bar is open.
        dispatch(
          actions.setLogPanelConfig(state.id, {
            columnFilters: filters,
            searchFilter: undefined,
            ...(state.config?.searchVisible === undefined && {
              searchVisible: true,
            }),
          }),
        );
      }
    },
    [liveColumnFilters, dispatch, state.id, state.config?.searchVisible],
  );

  const filterDisplayMode: FilterDisplayMode =
    state.config?.filterDisplayMode ?? "hide";

  const setFilterDisplayMode = React.useCallback(
    (mode: FilterDisplayMode) => {
      dispatch(
        actions.setLogPanelConfig(state.id, { filterDisplayMode: mode }),
      );
    },
    [dispatch, state.id],
  );

  // Tell the width reducer which columns exist. It owns what that means for the
  // layout: survivors reclaim a removed column's space, and any drag in flight
  // has its rollback rebased onto the columns that are actually left.
  React.useEffect(
    function trackColumnsForResize() {
      resizeDispatch({ type: "columnsChanged", columns: dynamicColumns });
    },
    [dynamicColumns],
  );

  // Compute colorMap, stage colors, and prepared filters once for all rows
  // instead of per-row.
  //
  // The ref carries the previous colorMap into the next rebuild so that
  // buildColorMap can preserve existing color indices — adding or removing
  // a filter never reshuffles colors on unrelated filters.
  const prevColorMapRef = React.useRef<Map<string, number>>(new Map());
  const colorMap = React.useMemo(
    () => buildColorMap(liveColumnFilters.filters, prevColorMapRef.current),
    [liveColumnFilters.filters],
  );
  React.useEffect(() => {
    prevColorMapRef.current = colorMap;
  }, [colorMap]);
  // Palette must be long enough to cover the largest assigned index, not just
  // the number of entries — indices can be gappy between a removal and the
  // next addition that fills the gap.
  const stageColors = React.useMemo(() => {
    let max = -1;
    for (const idx of colorMap.values()) {
      if (idx > max) {
        max = idx;
      }
    }
    return generateFilterStageColors(max + 1);
  }, [colorMap]);
  // When search is hidden, hand rows an empty PreparedFilters so highlight
  // spans stop rendering. Filter state itself is preserved for when search
  // re-opens.
  const preparedFilters = React.useMemo(
    () =>
      isSearchVisible
        ? prepareFilters(liveColumnFilters.filters, colorMap)
        : EMPTY_PREPARED_FILTERS,
    [isSearchVisible, liveColumnFilters.filters, colorMap],
  );

  const getItemKey = React.useCallback(
    function getItemKey(index: number) {
      const dataIndex = virtualToDataIndex(index, currentPage);
      // Read the sequence off the timestamp index, not the log cache. Going
      // through the cache would leave this key as `dataIndex` until that log
      // streams in and then flip it to the sequence, remounting the row inside
      // React's commit phase and re-entering the virtualizer's measure loop.
      return logManager?.getSequenceByIndex(dataIndex) ?? dataIndex;
    },
    [logManager, currentPage],
  );

  const logCountForCurrentPage = React.useMemo(() => {
    const startIndex = currentPage * MAX_LOGS_PER_PAGE;
    const remainingLogs = Math.max(0, panelLogCount - startIndex);
    return Math.min(remainingLogs, MAX_LOGS_PER_PAGE);
  }, [currentPage, panelLogCount]);

  const virtualizer = useVirtualizer({
    count: logCountForCurrentPage,
    // Value chosen to match the loading row height
    estimateSize: () => DEFAULT_LOG_LINE_HEIGHT,
    getItemKey,
    getScrollElement: () => tableRef.current,
    // Value chosen through trial and error
    overscan: 5,
    paddingStart: DEFAULT_LOG_LINE_HEIGHT, // account for header
    useAnimationFrameWithResizeObserver: true,
  });
  const items = virtualizer.getVirtualItems();

  useLogs(logManager, virtualizer, timer, currentPage, MAX_LOGS_PER_PAGE, {
    enabled: state.data.length > 0,
  });

  const scrollTable = React.useCallback(
    function scrollTable() {
      if (logManager === null) {
        return;
      }

      // Don't scroll when this panel has initiated a timer update
      if (hasInitiatedTimerUpdateRef.current) {
        hasInitiatedTimerUpdateRef.current = false;
        return;
      }

      // Closest index as of (equal to or before) currentTime
      const dataIndexAsOfTime = logManager.findLogIndexForTime(
        timer.currentTime,
      );
      const timeAsOfTime = logManager.getTimestampByIndex(dataIndexAsOfTime);

      // Calculate which page this index falls within
      const targetPage = Math.floor(dataIndexAsOfTime / MAX_LOGS_PER_PAGE);
      if (targetPage !== currentPage) {
        setCurrentPage(targetPage);
      }

      // Map to virtual index
      const indexWithinPage = dataIndexAsOfTime % MAX_LOGS_PER_PAGE;

      // 2025-04-02 (GM)
      // While the virtualizer exposes a "scrollToIndex" method,
      // under certain conditions that happen to match ours,
      // it will continuously, recursively call itself in an attempt to adjust for dynamic sizing.
      // https://github.com/TanStack/virtual/blob/main/packages/virtual-core/src/index.ts#L997-L1016
      //
      // Combing through relevant GH issues didn't provide a better resolution:
      //  - https://github.com/TanStack/virtual/issues/925
      //  - https://github.com/TanStack/virtual/issues/467
      const offsetAndAlign = virtualizer.getOffsetForIndex(
        indexWithinPage,
        "center",
      );
      if (offsetAndAlign === undefined) {
        return;
      }
      const [offset] = offsetAndAlign;
      virtualizer.scrollToOffset(offset);

      // Highlight every row sharing this timestamp as the playhead reaches it.
      if (timeAsOfTime !== undefined) {
        setSelected({ kind: "time", time: timeAsOfTime });
      }
    },
    [logManager, timer, virtualizer, currentPage],
  );

  /**
   * When log indices have changed, reset scroll state.
   */
  React.useEffect(scrollTable, [indexedLogCount, scrollTable]);

  React.useEffect(
    function setTimerCallbacks() {
      const abortController = new AbortController();

      timer.addListener("tick", scrollTable, {
        signal: abortController.signal,
      });
      timer.addListener("seek", scrollTable, {
        signal: abortController.signal,
      });

      return function abort() {
        abortController.abort();
      };
    },
    [timer, scrollTable],
  );

  const onRenderingSurfaceResize = React.useCallback<OnDimensionsChange>(() => {
    if (logManager && state.data.length > 0) {
      scrollTable();
    }
  }, [logManager, state.data.length, scrollTable]);

  const onRenderingSurfaceResizeDebounced =
    useDebouncedCallback<OnDimensionsChange>(onRenderingSurfaceResize, 150);

  const [measured] = useMeasure<HTMLDivElement>({
    onDimensionsChange: onRenderingSurfaceResizeDebounced,
  });

  const hasActiveColumnFilters = Object.values(liveColumnFilters.filters).some(
    (filter) =>
      filter.stages.some(
        (stage) =>
          isValuelessCondition(stage.condition) || stage.value.trim() !== "",
      ),
  );

  const onClearError = React.useCallback(() => setError(null), []);

  const onRowClick = React.useCallback(
    (logTime: bigint, sequence: number) => {
      hasInitiatedTimerUpdateRef.current = true;
      // A click selects just this row, even when other rows share its timestamp.
      setSelected({ kind: "row", sequence });
      timer.seekTo(logTime);
      setExpanded((prev) => {
        if (prev.has(sequence)) {
          const next = new Set(prev);
          next.delete(sequence);
          return next;
        }
        return new Set(prev).add(sequence);
      });
    },
    [timer],
  );

  const handlePageChange = React.useCallback(
    (page: number) => {
      if (logManager === null) {
        return;
      }
      setCurrentPage(page);

      // Get the first log of the new page
      const firstLogIndex = page * MAX_LOGS_PER_PAGE;
      const timestamp = logManager.getTimestampByIndex(firstLogIndex);

      if (timestamp !== undefined) {
        // Set flag to prevent scrollTable from handling this timer update
        hasInitiatedTimerUpdateRef.current = true;
        timer.seekTo(timestamp);
      }
    },
    [logManager, timer],
  );

  const totalVirtualHeight = virtualizer.getTotalSize();
  const scrollListInlineStyles = React.useMemo(() => {
    return {
      "--grid-template-columns": buildGridTemplate(
        dynamicColumns,
        columnWidths,
      ),
      // Feed the gap back into the variable the row grids consume, so the value
      // computeMinGridWidth sums is by construction the one actually rendered.
      "--column-gap": `${COLUMN_GAP_PX}px`,
      // Overflow the panel rather than compress columns below their floor;
      // .virtualized-grid's overflow:auto turns that into horizontal scroll.
      minWidth: `${computeMinGridWidth(dynamicColumns, columnWidths)}px`,
      height: `${totalVirtualHeight}px`,
    } as React.CSSProperties;
  }, [dynamicColumns, columnWidths, totalVirtualHeight]);

  const [isApplyingFilter, setIsApplyingFilter] = React.useState(false);
  // The pill renders from config as soon as the edit commits, but the
  // worker takes seconds to apply a filter over a large index — the tracker
  // owns the "applying" indicator's latest-wins/abort semantics.
  const filterApplyTracker = React.useMemo(
    () => createFilterApplyTracker(setIsApplyingFilter),
    [],
  );
  // Two aborts have no follow-up apply: the bar hiding, and the panel losing its
  // data (its index is disposed, so FilterBar never re-applies). Aborts
  // deliberately don't clear the indicator, so without this reset it would
  // stick on.
  React.useEffect(() => {
    if (!isSearchVisible || !hasData) {
      filterApplyTracker.reset();
    }
  }, [isSearchVisible, hasData, filterApplyTracker]);

  const onSearch = React.useCallback(
    (columnFilters: Record<string, ColumnFilter>, signal?: AbortSignal) => {
      if (logManager === null) {
        return;
      }
      // Persistence happens at commit time in columnFiltersDispatch; this
      // callback only applies the filters to the worker.
      const application = logManager.filterLogs(
        { columnFilters, displayMode: filterDisplayMode },
        signal,
      );
      filterApplyTracker.track(application);
      application.catch((err) => {
        if (isAbortError(err)) {
          return;
        }
        ErrorMonitoringService.captureError(err);
      });
    },
    [logManager, filterDisplayMode, filterApplyTracker],
  );

  return (
    <PanelLayout
      state={state}
      layout={layout}
      header={
        <PanelHeader
          additionalTools={
            <SearchTool
              isActive={isSearchVisible || hasActiveColumnFilters}
              onClick={toggleSearchVisibility}
            />
          }
          backgroundLoading={loadingState === "background"}
          className={styles.panelHeader}
          state={state}
        />
      }
    >
      <div className={styles.container} ref={measured}>
        <FilterBar
          hidden={!isSearchVisible}
          columnFilters={liveColumnFilters}
          columnFiltersDispatch={columnFiltersDispatch}
          colorMap={colorMap}
          stageColors={stageColors}
          filterDisplayMode={filterDisplayMode}
          onFilterDisplayModeChange={setFilterDisplayMode}
          onSearch={onSearch}
          isApplyingFilter={isApplyingFilter}
          searchIndexState={searchIndexState}
          hasData={hasData}
          needsBuildConfirmation={needsConfirmation}
          topicMessageCount={topicMessageCount}
          onBuildIndex={buildSearchIndex}
          onDismissBuildPrompt={toggleSearchVisibility}
        />
        <div
          className={styles.virtualizedGrid}
          ref={tableRef}
          style={VIRTUAL_GRID_INLINE_STYLES}
        >
          {hasData && hasActiveColumnFilters && panelLogCount === 0 && (
            <div className={styles.noMatches}>
              <Alert severity="info">No logs match the active filters</Alert>
            </div>
          )}
          <div className={styles.scrollList} style={scrollListInlineStyles}>
            <TableHeader
              columnDefinitions={dynamicColumns}
              canFilter={isSearchVisible}
              columnFilters={liveColumnFilters}
              columnFiltersDispatch={columnFiltersDispatch}
              colorMap={colorMap}
              stageColors={stageColors}
              panelId={state.id}
              headerRowRef={headerRowRef}
              onResizeStart={handleResizeStart}
              onResizeMove={handleResizeMove}
              onResizeEnd={handleResizeEnd}
              onResizeAbort={handleResizeAbort}
            />
            <div
              className={styles.window}
              style={{
                transform: `translateY(${items[0]?.start ?? 0}px)`,
              }}
            >
              {state.data.length > 0 &&
                items.map((virtualRow) => {
                  const dataIndex = virtualToDataIndex(
                    virtualRow.index,
                    currentPage,
                  );
                  const log = logManager?.getLogByIndex(dataIndex);
                  const columns = dynamicColumns.map((column) => ({
                    ...column,
                    isLoading:
                      // We're loading if the column doesn't exist in log and we're
                      // displaying the previous query while the new one is fetching.
                      column.columnId in (log?.data || {}) === false,
                  }));

                  return (
                    <Row
                      columns={columns}
                      preparedFilters={preparedFilters}
                      dimmed={
                        filterDisplayMode === "dim" &&
                        hasActiveColumnFilters &&
                        logManager !== null &&
                        !logManager.isMatching(dataIndex)
                      }
                      expanded={
                        log?.sequence !== undefined &&
                        expanded.has(log.sequence)
                      }
                      key={virtualRow.key}
                      log={log}
                      onClick={onRowClick}
                      ref={virtualizer.measureElement}
                      selected={isRowSelected(log, selected)}
                      stageColors={stageColors}
                      virtualIndex={virtualRow.index}
                    />
                  );
                })}
            </div>
          </div>
        </div>
        <Pagination
          currentPage={currentPage}
          disabled={loadingState !== false}
          onPageChange={handlePageChange}
          pageCount={Math.ceil(panelLogCount / MAX_LOGS_PER_PAGE)}
        />
        <NoDataMessage
          panelData={state.data}
          onAddDataAction={() => {
            // Open the sidebar and spotlight the columns field
            selectPanel(state.id);
            sidebar.openView<LogPanelSettingsSidebarProps>({
              type: SidebarViewType.PanelSettings,
              data: {
                field: "Columns",
                spotlightKey: performance.now(),
              },
            });
          }}
        />
        <DataNotFoundMessage panelId={state.id} />
        <RenderingError error={error} onClearError={onClearError} />
      </div>
    </PanelLayout>
  );
}
